import globalIP from "@/GlobelIP";

/**
 * Generate a fallback UUID if crypto.randomUUID is not available
 */
function generateUUID() {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Generate or retrieve a stable device ID
 */
export function getStableDeviceId() {
  let id = localStorage.getItem("deviceId");

  if (!id) {
    const cookieMatch = document.cookie.match(/deviceId=([^;]+)/);
    if (cookieMatch) {
      id = cookieMatch[1];
      localStorage.setItem("deviceId", id);
    }
  }

  if (!id) {
    if (window.crypto && window.crypto.randomUUID) {
      id = "DEV-" + window.crypto.randomUUID();
    } else {
      id = "DEV-" + generateUUID();
    }

    localStorage.setItem("deviceId", id);
    document.cookie = `deviceId=${id}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
  }

  return id;
}

/**
 * Check if the current user has access on this device
 */
export const checkUserDeviceAccess = async () => {
  try {
    const userId = localStorage.getItem("id");
    const deviceId = getStableDeviceId();

    if (!userId) {
      return { success: false, message: "Missing user information." };
    }

    // 1️⃣ Fetch user details
    const userRes = await fetch(`${globalIP}/api/user-details/${userId}`);
    if (!userRes.ok) {
      return { success: false, message: "Failed to fetch user details." };
    }

    const userData = await userRes.json();
    const user = userData?.data?.attributes;

    if (!user) {
      return { success: false, message: "User not found." };
    }

    // 2️⃣ Admin → always allow
    if (user.Category === "Admin") {
      return { success: true, message: "Admin user → access granted" };
    }

    // 3️⃣ Multiple devices allowed → always allow
    const allowMultiple = user.allowMultipleDevices ?? false;
    if (allowMultiple) {
      return { success: true, message: "Allowed (multiple devices enabled)" };
    }

    if (!deviceId) {
      return { success: false, message: "Device ID is required." };
    }

    // 4️⃣ Fetch user devices from backend
    const deviceRes = await fetch(`${globalIP}/api/device-access/user/${userId}`);
    if (!deviceRes.ok) {
      return { success: false, message: "Failed to fetch user devices." };
    }

    const deviceData = await deviceRes.json();
    const devices = deviceData.devices || [];

    // 5️⃣ Check if device exists
    const userDevice = devices.find(d => d.deviceId === deviceId);

    if (!userDevice) {
      return {
        success: false,
        status: "PENDING_APPROVAL",
        message: "This device is not registered. Waiting for admin approval.",
      };
    }

    // 6️⃣ Device exists → check if allowed
    if (!userDevice.isAllowed) {
      return {
        success: false,
        status: "NOT_ALLOWED",
        message: "Device access not allowed. Waiting for admin approval.",
      };
    }

    // 7️⃣ Device allowed → success
    return { success: true, message: "Device access allowed." };

  } catch (err) {
    console.error("🔥 Device check error:", err);
    return {
      success: false,
      message: "Something went wrong during device check.",
    };
  }
};
